import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useDocumentZoom } from '../components/document-zoom';
import { usePanToScroll } from '../components/use-pan-to-scroll';
import { cn } from '@/lib/utils';
import { ZoomControls } from '../components/ZoomControls';

// Vite bundles the worker via the `?url` import; react-pdf fetches the bytes
// cookie-authed, same-origin (the /extraction-proposal/source endpoint).
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
const PDF_OPTIONS = { withCredentials: true } as const;

/**
 * Left pane of the section review: the original uploaded document as a continuous
 * scrollable PDF, so the reviewer can check each section against the source. (Click-
 * to-highlight of the active section's page/box is a later pass — needs bbox provenance.)
 *
 * Zoom: the measured fit-to-width is the base; the zoom factor multiplies it, so 100% is
 * always "fits the pane" and the pane scrolls both ways past that.
 */
export function OriginalPdfPane({ sourceUrl }: { sourceUrl: string }) {
  // Measure the OUTER (non-scrolling) box, not the scroll container: past 100% the pages
  // grow wider than the pane, a horizontal scrollbar appears, the scroll container's
  // clientWidth shrinks, the base re-measures smaller, the pages shrink, the scrollbar
  // goes away, the width grows back… a visible flicker loop. The outer box's width does
  // not depend on its content, so the base is stable and zoom is a pure multiplier.
  const frameRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(520);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { zoom, zoomIn, zoomOut, reset, canIn, canOut } = useDocumentZoom(scrollRef);
  const pan = usePanToScroll(zoom > 1);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    // -32 for the page padding, -12 so a vertical scrollbar never forces a horizontal one at 100%.
    const measure = () => setPageWidth(Math.max(240, el.clientWidth - 32 - 12));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={frameRef} className="relative h-full min-h-0 min-w-0 overflow-hidden">
      <ZoomControls
        zoom={zoom}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onReset={reset}
        canIn={canIn}
        canOut={canOut}
      />
      {/* Past 100% the page overflows the pane: grab it and drag to move around (hand cursor),
          in addition to normal wheel/trackpad scrolling. `touch-none` while enabled so a
          finger-drag pans instead of scrolling the page behind. */}
      <div
        ref={scrollRef}
        data-testid="pdf-scroll"
        {...pan}
        className={cn(
          'h-full overflow-auto rounded-xl border border-line bg-[#eef1f5]',
          zoom > 1 && 'cursor-grab touch-none select-none data-[panning=true]:cursor-grabbing',
        )}
      >
        <Document
          file={sourceUrl}
          options={PDF_OPTIONS}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          loading={<div className="p-4 text-[13px] text-muted">Loading document&hellip;</div>}
          error={<div className="p-4 text-[13px] text-muted">Couldn&rsquo;t load the original document.</div>}
        >
          {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNo) => (
            // `justify-start` past 100%: a centred page wider than the pane would clip its
            // left edge with no way to scroll to it.
            <div key={pageNo} className={zoom > 1 ? 'flex w-max min-w-full justify-start p-3' : 'flex justify-center p-3'}>
              <div className="shadow-card">
                <Page
                  pageNumber={pageNo}
                  width={Math.round(pageWidth * zoom)}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
            </div>
          ))}
        </Document>
      </div>
    </div>
  );
}
